// src/modules/app-views/serviceOrders/pages/Document/tabs/ItemsTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Table, Button, Space, Tooltip, Popconfirm, Tag, Row, Col } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ToolOutlined,
  ShoppingOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import ItemModal from './ItemModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServiceOrdersItemsTab = ({ items = [], setItems }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading, viewMode } = useDocumentContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenAdd = useCallback(() => {
    setEditingItem(null);
    setEditingIndex(null);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((record, index) => {
    setEditingItem(record);
    setEditingIndex(index);
    setModalOpen(true);
  }, []);

  const handleRemoveItem = useCallback(
    index => {
      setItems(prev =>
        prev
          .filter((_, i) => i !== index)
          .map((item, i) => ({ ...item, sortOrder: i + 1 }))
      );
    },
    [setItems]
  );

  const handleModalSave = useCallback(
    itemData => {
      if (editingIndex !== null) {
        // Update existing item
        setItems(prev =>
          prev.map((item, i) => (i === editingIndex ? { ...item, ...itemData } : item))
        );
      } else {
        // Add new item
        setItems(prev => [...prev, { ...itemData, sortOrder: prev.length + 1 }]);
      }
      setModalOpen(false);
      setEditingItem(null);
      setEditingIndex(null);
    },
    [editingIndex, setItems]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const totals = calculateTotals(items);

  const columns = [
    {
      title: '#',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 50,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: translateX('type'),
      dataIndex: 'itemType',
      key: 'itemType',
      width: 80,
      align: 'center',
      render: (_, r) =>
        r?.itemType === 'service' ? (
          <Tooltip title={translateX('service')}>
            <ToolOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        ) : (
          <Tooltip title={translateX('product')}>
            <ShoppingOutlined style={{ color: '#52c41a' }} />
          </Tooltip>
        ),
    },
    {
      title: translateX('name'),
      dataIndex: 'name',
      key: 'name',
      className: 'text-truncate',
      render: (_, r) => (
        <div>
          <span className="font-weight-semibold">{r?.name || '—'}</span>
          {r?.code && (
            <Tag style={{ marginLeft: 6, fontSize: 10 }}>{r.code}</Tag>
          )}
        </div>
      ),
    },
    {
      title: translateX('quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: v => v || 1,
    },
    {
      title: translateX('unit_price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: v => formatCurrency(v),
    },
    {
      title: translateX('discount'),
      dataIndex: 'discountValue',
      key: 'discountValue',
      width: 100,
      align: 'right',
      render: v => (v ? formatCurrency(v) : '—'),
    },
    {
      title: translateX('subtotal'),
      key: 'lineTotal',
      width: 120,
      align: 'right',
      render: (_, r) => {
        const total = (r?.quantity || 1) * (r?.unitPrice || 0) - (r?.discountValue || 0);
        return <span className="font-weight-semibold">{formatCurrency(total)}</span>;
      },
    },
    {
      title: translateX('actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record, index) =>
        !isLocked && (
          <Space size={4}>
            <Tooltip title={translateX('edit')}>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(record, index)}
              />
            </Tooltip>
            <Popconfirm
              title={translateX('confirm_remove')}
              onConfirm={() => handleRemoveItem(index)}
              okText={translateX('yes')}
              cancelText={translateX('no')}
            >
              <Tooltip title={translateX('remove')}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="serviceOrderItems" isFetching={loading} data={data} />

      {/* ITEMS TABLE */}
      <Card
        title={<div className="text-muted">{translateX('items_tab')}</div>}
        extra={
          !isLocked && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAdd}>
              {translateX('add_item')}
            </Button>
          )
        }
      >
        <Table
          rowKey={record => record._id || record.sortOrder}
          columns={columns}
          dataSource={items}
          pagination={false}
          size="small"
          bordered
          locale={{ emptyText: translateX('no_items_added') }}
        />
      </Card>

      {/* TOTALS SUMMARY */}
      {items.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <Row gutter={[16, 8]} justify="end">
            <Col xs={24} sm={12} lg={8}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>{translateX('services_total')}:</span>
                <span>{formatCurrency(totals.servicesTotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>{translateX('products_total')}:</span>
                <span>{formatCurrency(totals.productsTotal)}</span>
              </div>
              {totals.discountTotal > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    color: '#ff4d4f',
                  }}
                >
                  <span>{translateX('discount_total')}:</span>
                  <span>-{formatCurrency(totals.discountTotal)}</span>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderTop: '2px solid #f0f0f0',
                  fontWeight: 'bold',
                  fontSize: 16,
                }}
              >
                <span>
                  <DollarOutlined style={{ marginRight: 4 }} />
                  {translateX('grand_total')}:
                </span>
                <span>{formatCurrency(totals.grandTotal)}</span>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* ITEM MODAL */}
      <ItemModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingItem(null);
          setEditingIndex(null);
        }}
        onSave={handleModalSave}
        editingItem={editingItem}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServiceOrdersItemsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Calculate totals from items array
 */
function calculateTotals(items) {
  let servicesTotal = 0;
  let productsTotal = 0;
  let discountTotal = 0;

  (items || []).forEach(item => {
    const lineTotal = (item.quantity || 1) * (item.unitPrice || 0);
    const lineDiscount = item.discountValue || 0;

    if (item.itemType === 'service') {
      servicesTotal += lineTotal - lineDiscount;
    } else {
      productsTotal += lineTotal - lineDiscount;
    }
    discountTotal += lineDiscount;
  });

  return {
    servicesTotal: Math.round(servicesTotal * 100) / 100,
    productsTotal: Math.round(productsTotal * 100) / 100,
    discountTotal: Math.round(discountTotal * 100) / 100,
    grandTotal: Math.round((servicesTotal + productsTotal) * 100) / 100,
  };
}

/**
 * Format number as BRL currency
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}
